import { ExternalLink, Loader2, AlertCircle, Brain } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { convertN8NPicks, type TechnicalPick } from "@/components/analysis/TechnicalPicksCard";
import { useAnalysis } from "@/hooks/useAnalysis";
import { OddsRow, resolveOddsForPick } from "@/lib/oddsEstimator";
import { supabase as supabasePublic } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { WORLD_CUP_OFFICIAL_LOGO } from "@/lib/worldCup2026";

interface SG7PredictionCardProps {
  fixtureId: number | null;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  leagueName?: string;
  leagueLogo?: string;
  matchDate?: string;
  gameStatus?: string;
  homeScore?: number | null;
  awayScore?: number | null;
  elapsed?: number | null;
  statusShort?: string | null;
}

export function SG7PredictionCard({
  fixtureId,
  homeTeamName,
  awayTeamName,
  homeTeamLogo,
  awayTeamLogo,
  leagueName,
  leagueLogo,
  matchDate,
  gameStatus,
  homeScore,
  awayScore,
  elapsed,
  statusShort,
}: SG7PredictionCardProps) {
  const navigate = useNavigate();
  const { analysis, loading } = useAnalysis(fixtureId);

  // Fetch odds data for combo estimation
  const { data: oddsData } = useQuery({
    queryKey: ['odds-full', fixtureId],
    queryFn: async () => {
      if (!fixtureId) return null;
      const { data, error } = await supabasePublic
        .from('odds')
        .select(`
          home_odds, draw_odds, away_odds,
          over_15_odds, under_15_odds,
          over_25_odds, under_25_odds,
          over_35_odds, under_35_odds,
          over_45_odds, under_45_odds,
          over_55_odds, under_55_odds,
          btts_yes_odds, btts_no_odds,
          dc_1x_odds, dc_12_odds, dc_x2_odds,
          home_over_25_odds, home_under_25_odds,
          draw_over_25_odds, draw_under_25_odds,
          away_over_25_odds, away_under_25_odds,
          h1_home_odds, h1_draw_odds, h1_away_odds,
          h1_over_05_odds, h1_over_15_odds,
          h1_under_05_odds, h1_under_15_odds
        `)
        .eq('fixture_id', fixtureId)
        .maybeSingle();

      if (error) return null;
      return data as OddsRow | null;
    },
    enabled: !!fixtureId,
    staleTime: 5 * 60 * 1000,
  });

  const isLive = gameStatus === 'live' || gameStatus === 'in_progress';
  const isFinal = gameStatus === 'finished' || gameStatus === 'completed';
  const showScore = (isLive || isFinal) && homeScore !== null && awayScore !== null;

  const matchTime = matchDate
    ? new Date(matchDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  const getStatusText = () => {
    if (isLive && elapsed) return statusShort === 'HT' ? 'HT' : `${elapsed}'`;
    if (isFinal) return statusShort || 'FT';
    return matchTime;
  };

  // Loading state
  if (loading) {
    return (
      <div className={cn(
        "rounded-xl overflow-hidden",
        "bg-[hsl(var(--sg7-surface-1))] border border-[hsl(var(--sg7-stroke-subtle))]"
      )}>
        <div className="flex items-center justify-center py-8 gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--sg7-gold))]" />
          <span className="text-sm text-[hsl(var(--sg7-text-muted))]">Loading SG7 analysis...</span>
        </div>
      </div>
    );
  }

  // Not available / error
  if (!analysis || analysis.status === 'error') {
    return (
      <div className={cn(
        "rounded-xl overflow-hidden",
        "bg-[hsl(var(--sg7-surface-1))] border border-dashed border-[hsl(var(--sg7-stroke-subtle))]"
      )}>
        <div className="flex items-center justify-center py-8 gap-3">
          <AlertCircle className="w-5 h-5 text-[hsl(var(--sg7-text-muted))]" />
          <span className="text-sm text-[hsl(var(--sg7-text-muted))]">SG7 analysis not available</span>
        </div>
      </div>
    );
  }

  // Processing
  if (analysis.status === 'pending' || analysis.status === 'processing') {
    return (
      <div className={cn(
        "rounded-xl overflow-hidden",
        "bg-[hsl(var(--sg7-surface-1))] border border-[hsl(var(--sg7-stroke-subtle))]"
      )}>
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <div className="rounded-full bg-[hsl(var(--sg7-gold))]/10 p-3 relative">
            <Brain className="w-6 h-6 text-[hsl(var(--sg7-gold))]" />
            <Loader2 className="w-3 h-3 text-[hsl(var(--sg7-gold))] absolute -bottom-0.5 -right-0.5 animate-spin" />
          </div>
          <span className="text-sm font-medium text-[hsl(var(--sg7-text))]">SG7 analysis in progress...</span>
        </div>
      </div>
    );
  }

  // Ready — picks
  const picks = analysis.ai_output?.picks ? convertN8NPicks(analysis.ai_output.picks) : [];

  return (
    <div className={cn(
      "rounded-xl overflow-hidden",
      "bg-[hsl(var(--sg7-surface-1))] border border-[hsl(var(--sg7-stroke-subtle))]",
      "sg7-glow-hover"
    )}>
      {/* Match header — same as PickMatchCard */}
      <div className="px-4 py-3 border-b border-[hsl(var(--sg7-stroke-subtle))]">
        {/* League row */}
        <div className="flex items-center gap-2 mb-2">
          {(() => {
            const isWC = !!leagueName && /world cup/i.test(leagueName);
            const logoSrc = isWC ? WORLD_CUP_OFFICIAL_LOGO : leagueLogo;
            if (!logoSrc) return null;
            return (
              <img
                src={logoSrc}
                alt={leagueName}
                className={cn(
                  "w-4 h-4 object-contain",
                  isWC ? "opacity-100" : "brightness-0 invert opacity-80"
                )}
              />
            );
          })()}
          {leagueName && (
            <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--sg7-text-subtle))] font-bold">
              {leagueName}
            </span>
          )}
          <span className="text-[10px] text-[hsl(var(--sg7-text-subtle))]">•</span>
          <span className={cn(
            "text-[10px] uppercase tracking-wide font-bold",
            isFinal ? "text-[hsl(var(--sg7-text-muted))]" :
            isLive ? "text-red-500 animate-pulse" :
            "text-[hsl(var(--sg7-gold))]"
          )}>
            {getStatusText()}
          </span>
        </div>

        {/* Teams row */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {homeTeamLogo && (
              <img src={homeTeamLogo} alt={homeTeamName} className="w-5 h-5 object-contain shrink-0" />
            )}
            <span className="text-sm font-bold text-[hsl(var(--sg7-text))] truncate">
              {homeTeamName}
            </span>
          </div>

          {showScore ? (
            <div className="flex items-center gap-1 shrink-0">
              <span className={cn(
                "text-lg font-black tabular-nums",
                isLive ? "text-red-500" : "text-[hsl(var(--sg7-text))]"
              )}>
                {homeScore}
              </span>
              <span className="text-sm font-bold text-[hsl(var(--sg7-text-muted))]">-</span>
              <span className={cn(
                "text-lg font-black tabular-nums",
                isLive ? "text-red-500" : "text-[hsl(var(--sg7-text))]"
              )}>
                {awayScore}
              </span>
            </div>
          ) : (
            <span className="text-xs text-[hsl(var(--sg7-text-muted))] font-bold shrink-0">vs</span>
          )}

          <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
            <span className="text-sm font-bold text-[hsl(var(--sg7-text))] truncate text-right">
              {awayTeamName}
            </span>
            {awayTeamLogo && (
              <img src={awayTeamLogo} alt={awayTeamName} className="w-5 h-5 object-contain shrink-0" />
            )}
          </div>
        </div>
      </div>

      {/* Picks list — same style as PickRow */}
      {picks.length > 0 && (
        <div className="p-3 space-y-2">
          {picks.map((pick, index) => (
            <SG7PickRow key={`${pick.label}-${index}`} pick={pick} oddsData={oddsData} />
          ))}
        </div>
      )}

      {/* View full analysis button */}
      <div className="px-3 pb-3">
        <button
          onClick={() => navigate(`/analysis/${fixtureId}`)}
          className={cn(
            "w-full flex items-center justify-center gap-1.5",
            "py-2 px-3 rounded-lg text-sm font-medium",
            "bg-[hsl(var(--sg7-surface-2))] text-[hsl(var(--sg7-text-subtle))]",
            "hover:text-[hsl(var(--sg7-gold))] hover:bg-[hsl(var(--sg7-surface-3))]",
            "transition-colors"
          )}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          View full analysis
        </button>
      </div>
    </div>
  );
}

/** Pick row matching the SG7 Picks page visual style */
function SG7PickRow({ pick, oddsData }: { pick: TechnicalPick; oddsData?: OddsRow | null }) {
  const isNoBet = pick.noBet || pick.type === 'no_bet';

  // Resolve odds using the estimator (supports combos)
  let displayOdds: number | null = null;
  let isEstimated = false;

  if (!isNoBet) {
    if (pick.pickId && oddsData) {
      const resolved = resolveOddsForPick(pick.pickId, pick.odds || null, oddsData, pick.label);
      if (resolved.value > 1.01) {
        displayOdds = resolved.value;
        isEstimated = resolved.source === 'EST';
      }
    } else if (pick.odds && pick.odds > 1) {
      displayOdds = pick.odds;
    }
  }

  const prefix = isEstimated ? '≈' : '@';

  return (
    <div className={cn(
      "flex items-center justify-between py-2 px-3 rounded-lg",
      "bg-[hsl(var(--sg7-surface-2))]",
      isNoBet && "opacity-50"
    )}>
      <div className="flex-1 min-w-0">
        <span className={cn(
          "text-sm font-medium text-[hsl(var(--sg7-text))] truncate block",
          isNoBet && "line-through"
        )}>
          {pick.label}
        </span>
      </div>

      <div className="flex items-center gap-2 ml-3 shrink-0">
        {displayOdds && (
          <span className="font-mono text-xs text-[hsl(var(--sg7-gold))]">
            {prefix}{displayOdds.toFixed(2)}
          </span>
        )}
        {isNoBet && (
          <span className="text-[10px] font-bold uppercase text-red-400">
            no bet
          </span>
        )}
      </div>
    </div>
  );
}
